import { Box, MenuItem, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Ingredient } from '../../apiTypes';
import DataTable, { Column } from '../../components/DataTable';
import StoreHistory from '../../components/History/StoreHistory';
import Image from '../../components/Image';
import StyledTextField from '../../components/styled/StyledTextField';
import { paperProps } from '../../constants/styles';
import { t } from '../../constants/translations';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import getQntLabel from '../../utils/getQntLabel';

type Row = Ingredient & {
  qnt: number;
};

const ResiduesList = () => {
  const navidate = useNavigate();
  const { storeId } = useParams();
  const { lang } = useLocale();
  const { stores, ingredients } = useCafe();
  const store = useMemo(() => stores.find((s) => s.id === storeId), [stores, storeId]);

  const rows: Row[] = useMemo(
    () =>
      store
        ? ingredients
            .filter((i) => store.residues[i.id])
            .map((i) => ({
              ...i,
              qnt: store.residues[i.id] || 0,
            }))
        : ingredients
            .filter((i) => stores.some((s) => s.residues[i.id]))
            .map((i) => ({
              ...i,
              qnt: stores.reduce((acc, s) => acc + (s.residues[i.id] || 0), 0),
            })),
    [ingredients, store, stores]
  );

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'imageURL',
        title: '',
        render: (r: Row) => <Image src={r.imageUrl} />,
        sx: { width: 40, pr: 0 },
      },
      {
        name: 'name',
        title: t.name[lang],
        align: 'left',
        render: (r: Row) => <Typography>{r.name}</Typography>,
      },
      {
        name: 'residues',
        title: t.residues[lang],
        align: 'left',
        render: (r: Row) => <Typography>{getQntLabel(r.qnt, r, lang)}</Typography>,
      },
      {
        name: 'history',
        title: t.history[lang],
        align: 'center',
        render: store ? (r: Row) => <StoreHistory store={store} productId={r.id} /> : undefined,
      },
    ],
    [lang, store]
  );

  return (
    <Box sx={{ maxWidth: 800, m: '0 auto' }}>
      <DataTable
        columns={columns}
        rows={rows}
        onGoBack={() => navidate('../')}
        searchBy="name"
        dataTestIdPrefix="residue"
        customHeader={
          <div>
            <StyledTextField
              select
              label={t.store[lang]}
              value={store?.id || 'all'}
              size="small"
              name="stores"
              sx={{ width: 202 }}
              SelectProps={{ MenuProps: { PaperProps: { sx: paperProps } } }}
              onChange={(e) => navidate(`../${e.target.value}`)}
            >
              <MenuItem value="all">{t.all_stores[lang]}</MenuItem>
              {stores.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </StyledTextField>
          </div>
        }
      />
    </Box>
  );
};

export default ResiduesList;
